import axios from 'axios';
import { message } from 'antd';
import { Http } from '@capacitor-community/http';
import settle from 'axios/unsafe/core/settle.js';
import AxiosError from 'axios/unsafe/core/AxiosError.js';
import AxiosHeaders from 'axios/unsafe/core/AxiosHeaders.js';
import resolveConfig from 'axios/unsafe/helpers/resolveConfig.js';
import utils from 'axios/unsafe/utils.js';
import CanceledError from 'axios/unsafe/cancel/CanceledError.js';
import { clearNativeAuthMirror } from 'utils/nativePreferences';

// 生产 / Capacitor：在 .env.production 中设置 REACT_APP_API_BASE_URL（完整 API 前缀，含 /api）
// 开发：不设置则仍为 '/api'，走 webpack-dev-server 代理，行为与改前一致
const rawApiBase = process.env.REACT_APP_API_BASE_URL;
const apiBaseURL =
  typeof rawApiBase === 'string' && rawApiBase.trim() !== ''
    ? rawApiBase.trim().replace(/\/+$/, '')
    : '/api';

/** App 环境：window.Capacitor 存在即走原生 Http（与文档约定一致） */
function isCapacitorApp() {
  return typeof window !== 'undefined' && window.Capacitor != null;
}

/** 默认 xhr adapter（避免递归使用 apiClient 上挂的自定义 adapter） */
const xhrAdapter = axios.getAdapter(['xhr']);

/**
 * 将 headers 转为原生桥接可接受的 string 映射
 */
function headersToPlainStringMap(headers) {
  const json = AxiosHeaders.from(headers).toJSON();
  const out = {};
  Object.keys(json).forEach(key => {
    const v = json[key];
    if (v === undefined || v === null) return;
    out[key] = typeof v === 'string' ? v : String(v);
  });
  return out;
}

/**
 * axios responseType → @capacitor-community/http responseType
 */
function mapResponseType(rt) {
  if (rt === 'arraybuffer' || rt === 'blob' || rt === 'json' || rt === 'text' || rt === 'document') {
    return rt;
  }
  return undefined;
}

/**
 * Capacitor 桥接使用结构化克隆传参：AbortSignal、函数、部分 DOM 等会触发 DataCloneError。
 * JSON 请求体做一次 JSON 往返，去掉不可序列化字段，与后端收到的 JSON 一致。
 */
function cloneSerializableRequestData(data) {
  if (data == null || typeof data === 'string') return data;
  if (typeof data !== 'object') return data;
  try {
    return JSON.parse(JSON.stringify(data));
  } catch (e) {
    return data;
  }
}

/**
 * Capacitor 下：FormData 经 JS 桥无法按文件体原样交给原生 multipart；退回 xhr（若服务端已配 CORS 则仍可用）
 */
function createCapacitorHttpAdapter() {
  return function capacitorHttpAdapter(config) {
    if (!isCapacitorApp()) {
      return xhrAdapter(config);
    }

    return new Promise((resolve, reject) => {
      let _config;
      try {
        _config = resolveConfig(config);
      } catch (e) {
        reject(e);
        return;
      }

      const { url, method, data, timeout, signal, responseType } = _config;
      const headersPlain = headersToPlainStringMap(_config.headers);

      if (signal && signal.aborted) {
        reject(new CanceledError(null, config));
        return;
      }

      const onAbort = () => {
        reject(new CanceledError(null, config));
      };
      if (signal) {
        signal.addEventListener('abort', onAbort);
      }

      const done = () => {
        if (signal) {
          signal.removeEventListener('abort', onAbort);
        }
      };

      // multipart + 文件：原生桥无法传 Blob；走 WebView XHR（需服务端 CORS 放行 App 源）
      if (data != null && utils.isFormData(data)) {
        xhrAdapter(config).then(
          v => {
            done();
            resolve(v);
          },
          err => {
            done();
            reject(err);
          }
        );
        return;
      }

      const runNative = async () => {
        try {
          // 禁止把 AbortSignal 放进 webFetchExtra：postMessage 无法克隆，会导致详情页等带 signal 的请求直接 DataCloneError
          const safeData = cloneSerializableRequestData(data);
          const res = await Http.request({
            url,
            method: (method || 'get').toUpperCase(),
            headers: headersPlain,
            data: safeData,
            connectTimeout: timeout,
            readTimeout: timeout,
            responseType: mapResponseType(responseType),
            webFetchExtra: { credentials: 'include' },
          });

          const response = {
            data: res.data,
            status: res.status,
            statusText: String(res.status),
            headers: AxiosHeaders.from(res.headers || {}),
            config,
            request: {},
          };

          settle(
            v => {
              resolve(v);
              done();
            },
            err => {
              reject(err);
              done();
            },
            response
          );
        } catch (err) {
          done();
          reject(
            new AxiosError(
              err?.message || 'Network Error',
              AxiosError.ERR_NETWORK,
              config,
              null,
              null
            )
          );
        }
      };

      runNative();
    });
  };
}

const apiClient = axios.create({
  baseURL: apiBaseURL,
  timeout: 15000, // 增加超时时间到15秒，给DNS解析更多时间
  headers: {
    'Content-Type': 'application/json',
  },
  adapter: createCapacitorHttpAdapter(),
  // 添加重试配置
  validateStatus: function (status) {
    return status >= 200 && status < 300; // 默认行为
  },
});

// 请求拦截器
apiClient.interceptors.request.use(
  config => {
    // 检查网络状态
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return Promise.reject(new Error('网络连接已断开，请检查网络设置'));
    }
    
    // 判断是否是admin相关请求（通过URL路径）
    const isAdminUrl = config.url?.startsWith('/admin');
    const isAuthUrl = config.url === '/admin/login' || config.url === '/admin/register';
    
    // 登录/注册不携带 token
    if (isAuthUrl) {
      config.headers['X-Admin-Request'] = 'true';
      return config;
    }

    // 判断是否是B端请求（通过URL或请求数据中的admin标识）
    // 确保 config.data 是对象才检查 admin 属性
    const dataAdmin = config.data && typeof config.data === 'object' && !Array.isArray(config.data) 
      ? config.data.admin 
      : undefined;
    const isAdminRequest = isAdminUrl || dataAdmin === true || dataAdmin === 'true';
    
    let value;
    try {
      // B端使用admin_token，C端使用_pwd
      value = isAdminRequest 
        ? JSON.parse(localStorage.getItem('admin_token') || 'null')
        : JSON.parse(localStorage.getItem('_pwd') || 'null');
    } catch (err) {
      console.log(err, 'err')
    }

    if (value) {
      config.headers.token = value;
    }
    
    if (isAdminRequest) {
      config.headers['X-Admin-Request'] = 'true';
    }
    
    // 重置重试计数（如果存在）
    if (config.__retryCount !== undefined) {
      delete config.__retryCount;
    }
    
    return config;
  },
  error => {
    // 对请求错误做些什么
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  response => {
    return response.data;
  },
  async error => {
    // 忽略取消的请求错误
    if (axios.isCancel(error) || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }
    
    // 处理网络错误（DNS解析失败、网络不可达等）
    const isNetworkError = 
      !error.response && 
      (error.code === 'ERR_NAME_NOT_RESOLVED' || 
       error.code === 'ERR_NETWORK' || 
       error.code === 'ERR_INTERNET_DISCONNECTED' ||
       error.message?.includes('Network Error') ||
       error.message?.includes('ERR_NAME_NOT_RESOLVED'));
    
    if (isNetworkError) {
      const config = error.config || {};
      
      // 重试机制：最多重试3次
      const retryCount = config.__retryCount || 0;
      const maxRetries = 3;
      const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // 指数退避，最大5秒
      
      if (retryCount < maxRetries) {
        config.__retryCount = retryCount + 1;
        
        // 等待后重试
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        
        // 检查网络状态
        if (navigator.onLine) {
          console.log(`网络错误，正在重试 (${retryCount + 1}/${maxRetries})...`);
          // 只在第一次重试时提示用户
          if (retryCount === 1) {
            message.loading('网络连接异常，正在重试...', 1);
          }
          return apiClient(config);
        } else {
          // 网络离线，提示用户
          message.error('网络连接已断开，请检查网络设置后重试', 3);
          return Promise.reject(error);
        }
      } else {
        // 重试次数用完，提示用户
        console.error('网络错误，重试失败:', error);
        message.error('网络连接失败，请检查网络设置或稍后重试', 3);
        return Promise.reject(error);
      }
    }
    
    if (error.response && error.response.status === 401) {
      const url = error.config?.url || '';
      if (url === '/admin/login' || url === '/admin/register') {
        // 登录/注册接口 401 不跳转，由页面提示
        return Promise.reject(error);
      }
      const isAdminRequest = error.config?.headers?.['X-Admin-Request'] === 'true';
      if (!isAdminRequest) {
        try {
          localStorage.removeItem('_pwd');
        } catch (e) {
          /* ignore */
        }
        clearNativeAuthMirror();
      }
      if (isAdminRequest) {
        window.location.href = '/admin/login?relogin=1';
      } else {
        window.location.href = '/login?relogin=1';
      }
    }
    
    console.error(error);
    return Promise.reject(error);
  }
);

// 封装 get 请求
export const get = (url, params = {}, config = {}) => {
  return apiClient.get(url, { params, ...config });
};

// 封装 post 请求
export const post = (url, data = {}, config = {}) => {
  return apiClient.post(url, data, { ...config });
};

// 封装文件上传请求（假设服务器接受 multipart/form-data 格式）
export const upload = (url, formData, config = {}) => {
  // 注意：formData 是一个 FormData 对象，不是普通的 JSON 对象
  return apiClient.post(url, formData, {
    headers: {
      ...apiClient.defaults.headers,
      'Content-Type': 'multipart/form-data',
    },
    ...config,
  });
};

